import { Component, ErrorInfo, ReactNode } from "react";

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Catches runtime errors in the route subtree (lazy-chunk load failures,
 * render-time exceptions, WebGL init failures, etc.) and renders a
 * graceful fallback instead of leaving the user on a blank screen.
 *
 * Positioned inside PageTransition + SectionProvider but around <Routes>,
 * so Nav and Footer stay interactive — users can navigate to a different
 * route to recover without a full page reload.
 *
 * Must be a class component: React's error-catching lifecycle
 * (`getDerivedStateFromError`, `componentDidCatch`) has no hooks equivalent.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        // Swallowed in production; useful signal during development.
        // Replace with a remote logger (Sentry/LogRocket) if/when added.
        // eslint-disable-next-line no-console
        console.error("ErrorBoundary caught:", error, info.componentStack);
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        // Hard navigation so the ErrorBoundary state resets.
        window.location.href = "/";
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        return (
            <main
                role="alert"
                className="stage-bleed z-[50] flex items-center justify-center py-[var(--bleed)] bg-surface-dark text-white px-6"
            >
                <div className="max-w-md w-full text-center">
                    <h1 className="font-dahlia font-medium text-[clamp(32px,6vw,64px)] leading-none mb-6">
                        Something went wrong.
                    </h1>
                    <p className="text-[clamp(14px,1.5vw,16px)] font-light opacity-70 mb-10 leading-relaxed">
                        An unexpected error kept this page from loading.
                        Reload to try again, or head back to the home page.
                    </p>
                    <div className="flex gap-[2em] justify-center font-sans text-ui font-[400] sm:font-[300] tracking-wide">
                        <button
                            type="button"
                            onClick={this.handleReload}
                            className="opacity-50 hover:opacity-100 transition-opacity"
                        >
                            Reload
                        </button>
                        <button
                            type="button"
                            onClick={this.handleGoHome}
                            className="opacity-50 hover:opacity-100 transition-opacity"
                        >
                            Return home
                        </button>
                    </div>
                </div>
            </main>
        );
    }
}
